import React, { useEffect } from 'react';
import { Typography } from '@mui/material';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import {useDropzone} from 'react-dropzone';
import styled from 'styled-components';
import * as XLSX from "xlsx";
import { primary } from "../../assets/colorPallet";
import { cardStyle } from "../Dashboard/customStyles";

const getColor = (props) => {
  if (props.isDragAccept) {
      return '#00e676';
  }
  if (props.isDragReject) {
      return '#ff1744';
  }
  if (props.isFocused) {
      return '#2196f3';
  }
  return '#cccccc';
}

const Container = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 45px;
  border-width: 2px;
  border-radius: 2px;
  border-color: ${props => getColor(props)};
  border-style: dashed;
  background-color: #fafafa;
  color: #777777;
  outline: none;
  transition: border .24s ease-in-out;
`;

const SelectFile = (props) => {
  const {
    acceptedFiles,
    getRootProps,
    getInputProps,
    isFocused,
    isDragAccept,
    isDragReject
  } = useDropzone({
    maxFiles: 1,
    accept: {'application/vnd.ms-excel': ['.xlsx', '.xls', '.xlsm', '.xlsb']},
  });
  const { type, setData } = props;

  const handleDownload = () => {
    let uri = window.location.href;
    uri = uri.split("/")[0] + (type === "BS" ? "/BalanceSheet.xlsx" : "/IncomeStatement.xlsx");   //get the Excel file url
    let link = document.createElement('a');
    link.href = uri;
    link.download = (type === "BS" ? "Balance Sheet.xlsx" : "Income Statement.xlsx");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    if(!acceptedFiles.length) return;   //return when no file selected
    /* Boilerplate to set up FileReader */
    const reader = new FileReader();
    const rABS = !!reader.readAsBinaryString;
    reader.onload = e => {
      /* Parse data */
      const bstr = e.target.result;
      const wb = XLSX.read(bstr, { type: rABS ? "binary" : "array" });
      /* Get first worksheet */
      const wsname = wb.SheetNames[0];
      const ws = wb.Sheets[wsname];
      /* Convert array of arrays */
      const data = XLSX.utils.sheet_to_json(ws, { header: 1 });
      /* Update state */
      setData(data);
    };
    if (rABS) reader.readAsBinaryString(acceptedFiles[0]);
    else reader.readAsArrayBuffer(acceptedFiles[0]);
  }, [acceptedFiles.length]);

  return (
    <Card sx={cardStyle.excelCard}>
      <CardHeader
        sx={{ background: primary}}
        titleTypographyProps={{ fontSize: 35, fontWeight: 700, color: "white",  textAlign: "center" }}
        title={type === "BS" ? "Balance Sheet" : "Income Statement"}
      />
      <CardContent sx={{ padding: "30px 30px" }}>
        <Container {...getRootProps({isFocused, isDragAccept, isDragReject})}>
          <input {...getInputProps()} />
          {acceptedFiles.length ?
            <p>{acceptedFiles[0].name}</p>
            :
            isDragReject ?
              <p>Failed. Just select one file</p>
              :
              <p>Drag and drop your Excel file here, or click to select a file</p>
          }
        </Container>
        <div className="mt-3" />
        <Typography variant="h6" className="text-black-600 font-bold pl-5">
          The statement must meet the specific conditions below.
          <button className="text-blue-600 font-bold text-sm ml-16" onClick={handleDownload}>
            Download a Template&nbsp;&nbsp;&nbsp;<CloudDownloadIcon />
          </button>
          <p className="text-base pl-3">The dates should be in the first row and set at monthly or yearly intervals.</p>
          <p className="text-base pl-3">All categories and account items should be listed in the first column.</p>
          <p className="text-base pl-3">After the account items for a specific category, there should be a row for the total or a blank line.</p>
          <p className="text-base pl-3">
            {type === "BS" ?
              "The category names: Current Assets, Fixed Assets, Current Liabilities, Long-Term Liabilities, Equity"
            :
              "The category names: Income, Cost of Sales, Expenses, Other Income, Other Expenses"
            }
          </p>
        </Typography>
      </CardContent>
    </Card>
  );
}

export default SelectFile;